import { useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Building2, Calendar, Award, TrendingUp, Edit2, Save, X } from 'lucide-react';
import { Staff, updateStaff } from '@/services/staffService';

interface StaffDetailsSheetProps {
  staff: Staff | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export function StaffDetailsSheet({ staff, isOpen, onClose, onUpdate }: StaffDetailsSheetProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: staff?.name || '',
    email: staff?.email || '',
    role: staff?.role || '',
    department: staff?.department || '',
    status: staff?.status || 'active',
    location: staff?.location || 'Office',
    totalCapacityHours: staff?.totalCapacityHours || 40,
    availableCapacityHours: staff?.availableCapacityHours || 40,
  });

  if (!staff) return null;

  const capacityUsedPercent = Math.round(((staff.totalCapacityHours - staff.availableCapacityHours) / staff.totalCapacityHours) * 100);

  // Extract certifications from skills evidence
  const certifications = staff.skills
    .filter(s => s.evidence?.certificateName)
    .map(s => s.evidence!.certificateName!);

  // Reset form when staff changes or when starting edit
  const startEdit = () => {
    setFormData({
      name: staff.name,
      email: staff.email,
      role: staff.role,
      department: staff.department,
      status: staff.status,
      location: staff.location,
      totalCapacityHours: staff.totalCapacityHours,
      availableCapacityHours: staff.availableCapacityHours,
    });
    setIsEditing(true);
    setError(null);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      await updateStaff(staff.id, {
        ...staff,
        ...formData,
      });

      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update employee');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {staff.name}
            </div>
            {!isEditing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={startEdit}
                className="text-primary-500 border-primary-500 hover:bg-primary-500/10"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelEdit}
                  disabled={isSubmitting}
                  className="text-neutral-400 border-neutral-600 hover:bg-neutral-800"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={isSubmitting}
                  className="bg-primary-500 text-black hover:bg-primary-600"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </SheetTitle>
          <SheetDescription>Employee details and capacity information</SheetDescription>
        </SheetHeader>

        {error && (
          <div className="mt-4 p-3 bg-red-900/20 border border-red-500/50 rounded-md text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="mt-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white">Basic Information</h3>
            {!isEditing ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-400">Role:</span>
                  <span className="text-white">{staff.role}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-400">Email:</span>
                  <span className="text-white">{staff.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-400">Department:</span>
                  <span className="text-white">{staff.department}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-400">Status:</span>
                  <span className="text-white capitalize">{staff.status}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-400">Location:</span>
                  <span className="text-white">{staff.location}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name *</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="bg-neutral-900 border-neutral-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email *</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="bg-neutral-900 border-neutral-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Role *</Label>
                  <Input
                    id="edit-role"
                    value={formData.role}
                    onChange={(e) => handleChange('role', e.target.value)}
                    className="bg-neutral-900 border-neutral-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-department">Department *</Label>
                  <Input
                    id="edit-department"
                    value={formData.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                    className="bg-neutral-900 border-neutral-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-status">Status</Label>
                  <select
                    id="edit-status"
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-md text-white"
                  >
                    <option value="active">Active</option>
                    <option value="on-leave">On Leave</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-location">Location</Label>
                  <select
                    id="edit-location"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-md text-white"
                  >
                    <option value="Office">Office</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Field">Field</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Capacity */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white">Capacity Allocation</h3>
            {!isEditing ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400">Total Capacity:</span>
                  <span className="text-white">{staff.totalCapacityHours} hours/week</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400">Available:</span>
                  <span className="text-white">{staff.availableCapacityHours} hours/week</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400">Utilization:</span>
                  <span className="text-white">{capacityUsedPercent}%</span>
                </div>
                <div className="w-full bg-neutral-800 rounded-full h-2.5">
                  <div
                    className="bg-primary-500 h-2.5 rounded-full transition-all"
                    style={{ width: `${capacityUsedPercent}%` }}
                  />
                </div>
                <p className="text-xs text-neutral-500 italic">
                  Utilization = (Total - Available) / Total × 100
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-total-capacity">Total Capacity (hours/week) *</Label>
                  <Input
                    id="edit-total-capacity"
                    type="number"
                    value={formData.totalCapacityHours}
                    onChange={(e) => handleChange('totalCapacityHours', parseInt(e.target.value) || 0)}
                    className="bg-neutral-900 border-neutral-700"
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-available-capacity">Available Capacity (hours/week) *</Label>
                  <Input
                    id="edit-available-capacity"
                    type="number"
                    value={formData.availableCapacityHours}
                    onChange={(e) => handleChange('availableCapacityHours', parseInt(e.target.value) || 0)}
                    className="bg-neutral-900 border-neutral-700"
                    min="0"
                  />
                </div>
                <div className="p-3 bg-neutral-900 rounded-md border border-neutral-700">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-neutral-400">Calculated Utilization:</span>
                    <span className="text-white font-semibold">
                      {Math.round(((formData.totalCapacityHours - formData.availableCapacityHours) / formData.totalCapacityHours) * 100) || 0}%
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500">
                    Shows how much capacity is currently allocated
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Skills */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {staff.skills.length > 0 ? (
                staff.skills.map((skill) => (
                  <Badge key={skill.skillId} variant="secondary" className="bg-neutral-800 text-primary-400 border-neutral-700">
                    {skill.skillName}
                    <span className="ml-1 text-neutral-500 text-xs">({skill.proficiencyLevel})</span>
                  </Badge>
                ))
              ) : (
                <p className="text-sm text-neutral-400">No skills assigned</p>
              )}
            </div>
          </div>

          {/* Certifications */}
          {certifications.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Award className="h-4 w-4" />
                Certifications
              </h3>
              <ul className="space-y-2">
                {certifications.map((cert, index) => (
                  <li key={index} className="text-sm text-neutral-400 flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                    {cert}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Processes */}
          {staff.processes.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-white">Process Ownership</h3>
              <div className="flex flex-wrap gap-2">
                {staff.processes.map((process, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {process}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
